import React from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import Layout from "../Layout"
import PageHero from "../PageHero"
import News from "../News"
import RelatedSolutions from "../RelatedSolutions"
import "../../styles/news.scss"
import "../../styles/caseStudy.scss"

/**
 * Shared template for case study pages — extracted from the five existing
 * hand-built pages (src/pages/caseStudies/projects/{muscatMediaGroup,
 * bigJerrysFencing, nurturefulPsychology, emeAquaticSystems,
 * orangelinePakistan}.js), which are UNCHANGED by this template's
 * existence: each still renders its own markup inline. This component is
 * used only by the seven new case study pages.
 *
 * Markup/classes match the existing pages exactly (vm-news-section,
 * cs-intro, cs-challenge-grid, cs-solution-layout, cs-results-grid — see
 * caseStudy.scss), with two differences, both deliberate:
 *  - Photos render via GatsbyImage (lazy-loaded, responsive), not a plain
 *    <img> from a static import — the five existing pages all use a plain
 *    <img>; this template was asked to use gatsby-plugin-image instead,
 *    specifically for the new pages.
 *  - clientLogoSrc may be omitted (TTI Testing Laboratories has no logo
 *    file in src/images/clients/) — the client's name renders as text in
 *    the same position instead of an <img>, via .cs-client-logo--text
 *    (a small addition to caseStudy.scss; everything else there is
 *    unchanged).
 *
 * None of the five existing pages carry a "want to discover more / other
 * case studies" section, or any JSON-LD — there was no existing pattern to
 * follow for either. `relatedLinks` below reuses the same generic
 * RelatedSolutions component those pages use for cross-links to offers,
 * just with a "Want to discover more?" heading and case-study links
 * instead.
 */
const CaseStudyTemplate = ({
  pageTitle,
  metaDescription,
  clientName,
  bannerSummary,
  introHeading,
  heroImage,
  heroImageAlt,
  clientLogoSrc,
  about,
  challengesIntro,
  challenges = [],
  solutionsIntro,
  solutions = [],
  resultsIntro,
  metrics = [],
  outcomes = [],
  relatedLinks,
}) => (
  <div className="CaseStudeies">
    <Layout pageTitle={pageTitle} descrip={metaDescription}>
      <PageHero eyebrow="Customer Story" title={clientName} description={bannerSummary} />

      {/* About */}
      <section className="vm-news-section vm-news-section--light">
        <div className="vm-news-inner cs-intro">
          <div className="cs-intro__img">
            {heroImage && <GatsbyImage image={heroImage} alt={heroImageAlt} loading="lazy" />}
          </div>
          <div className="cs-intro__text">
            {clientLogoSrc ? (
              <img src={clientLogoSrc} alt={`${clientName} logo`} className="cs-client-logo" />
            ) : (
              <span className="cs-client-logo cs-client-logo--text">{clientName}</span>
            )}
            <h2 className="vm-news-heading vm-news-heading--dark">{introHeading}</h2>
            <p className="vm-news-sub vm-news-sub--dark">{about}</p>
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="vm-news-section vm-news-section--gray">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">What They Were Up Against</h2>
            <p className="vm-news-sub vm-news-sub--dark">{challengesIntro}</p>
          </div>
          <div className="cs-challenge-grid">
            {challenges.map(c => (
              <div key={c.title} className="cs-challenge-card">
                <div className="cs-challenge-card__icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <h3 className="cs-challenge-card__title">{c.title}</h3>
                <p className="cs-challenge-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="vm-news-section vm-news-section--light">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">What We Built</h2>
            <p className="vm-news-sub vm-news-sub--dark">{solutionsIntro}</p>
          </div>
          <div className="cs-solution-layout">
            <div className="cs-solutions-grid">
              {solutions.map(s => (
                <div key={s.num} className="cs-solution-item">
                  <div className="cs-solution-item__badge">{s.num}</div>
                  <div>
                    <span className="cs-solution-item__text">{s.text}</span>
                    <p className="cs-solution-item__desc">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="cs-solution-img">
              {heroImage && <GatsbyImage image={heroImage} alt={heroImageAlt} loading="lazy" />}
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="vm-news-section vm-news-section--gray">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">A Dramatic Transformation</h2>
            <p className="vm-news-sub vm-news-sub--dark">{resultsIntro}</p>
          </div>
          <div className="cs-results-grid">
            {metrics.map(r => (
              <div key={r.label} className="cs-result-card">
                <span className="cs-result-card__metric">{r.metric}</span>
                <p className="cs-result-card__label">{r.label}</p>
              </div>
            ))}
            {outcomes.map(o => (
              <div key={o} className="cs-result-card cs-result-card--bullet">
                <div className="cs-result-card__icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <p className="cs-result-card__label">{o}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <News isAbout={true} />
      {relatedLinks && relatedLinks.length > 0 && (
        <RelatedSolutions heading="Want to discover more?" links={relatedLinks} />
      )}
    </Layout>
  </div>
)

export default CaseStudyTemplate
